import { IExpression } from "../Models/IExpression";
import { IExpressionEvaluatorContext } from "../Models/IExpressionEvaluatorContext";
import { IExpressionEvaluatorRoot } from "../Models/IExpressionEvaluatorRoot";
import { IVariableScope } from "../Models/IVariableScope";
import { GlobalCompositeExpressionEvaluator } from "./CompositeExpressionEvaluator";
import { ObjectVariableScope } from "./VariableScopes/ObjectVariableScope";

export class ExpressionEvaluatorContext implements IExpressionEvaluatorContext {
  private parent: IExpressionEvaluatorContext | null = null;
  private variableScope: IVariableScope;
  private evaluator: IExpressionEvaluatorRoot<IExpression>;
  private properties: Record<string, any> = {};

  constructor(variableScope?: IVariableScope, evaluator?: IExpressionEvaluatorRoot<IExpression>) {
    this.variableScope = variableScope ?? new ObjectVariableScope();
    this.evaluator = evaluator ?? GlobalCompositeExpressionEvaluator;
  }

  SetProperty<T>(name: string, value: T): void {
    this.properties[name] = value;
  }

  GetProperty<T>(name: string): T {
    const value = this.properties[name];
    if (value === undefined) {
      throw new Error(`Property '${name}' not found in ExpressionEvaluatorContext`);
    }
    return value as T;
  }

  get Id(): string {
    return this.variableScope.id;
  }

  get StableId(): string {
    return this.variableScope.stableId;
  }

  get Parent(): IExpressionEvaluatorContext | null {
    return this.parent;
  }

  get Properties(): Record<string, any> {
    return this.properties;
  }

  get VariableScope(): IVariableScope {
    return this.variableScope;
  }

  get Evaluator(): IExpressionEvaluatorRoot<IExpression> {
    return this.evaluator;
  }

  CreateChild(variableScope?: IVariableScope): IExpressionEvaluatorContext {
    return new ChildExpressionEvaluatorContext(this, variableScope);
  }
}

class ChildExpressionEvaluatorContext implements IExpressionEvaluatorContext {
  private parent: IExpressionEvaluatorContext;
  private variableScope: IVariableScope;
  private properties: Record<string, any> = {};

  constructor(parent: IExpressionEvaluatorContext, variableScope?: IVariableScope) {
    this.parent = parent;
    this.variableScope = variableScope ?? new ObjectVariableScope({}, parent.VariableScope);
  }

  SetProperty<T>(name: string, value: T): void {
    this.properties[name] = value;
  }

  GetProperty<T>(name: string): T {
    // Check overrides first
    if (this.properties.hasOwnProperty(name)) {
      return this.properties[name] as T;
    }
    return this.parent.GetProperty<T>(name);
  }

  get Id(): string {
    return this.variableScope.id;
  }

  get StableId(): string {
    return this.variableScope.stableId;
  }

  get Parent(): IExpressionEvaluatorContext | null {
    return this.parent;
  }

  get VariableScope(): IVariableScope {
    return this.variableScope;
  }

  get Evaluator(): IExpressionEvaluatorRoot<IExpression> {
    return this.parent.Evaluator;
  }

  get Properties(): Record<string, any> {
    return { ...this.parent.Properties, ...this.properties };
  }

  CreateChild(variableScope?: IVariableScope): IExpressionEvaluatorContext {
    return new ChildExpressionEvaluatorContext(this, variableScope);
  }
}
